// PlannioLife Theme Engine - theme registry.
// A theme is a reusable "asset pack + metadata" descriptor. Adding a future theme means adding an
// entry here (plus its Planni artwork under /themes) - no application logic changes required.
// Themes are COSMETIC ONLY: they retint accent tokens and layer decorative atmosphere, Planni,
// seasonal greetings, empty-state art and celebrations. Layout, navigation, typography and status
// colours never change. Every theme supports Light and Dark mode.
//
// Palettes follow the PRD colour direction. Per the PRD these are creative direction: we derive
// lighter/darker tints and transparencies for hierarchy and accessibility. Functional colours
// (success/warning/error/info) keep PlannioLife's core design system and are never re-themed.

// season window months are 1-12; a window may wrap the year end (e.g. Nov -> Feb).
export const THEMES = [
  {
    id: "default",
    name: "Default",
    emoji: "🏡",
    description: "The original PlannioLife look.",
    inspiration: "Modern, warm, trustworthy.",
    seasonal: false,
    planni: "/themes/planni-default-wave.png",
    poses: { wave: "/themes/planni-default-wave.png", think: "/themes/planni-default-think.png", cheer: "/themes/planni-default-cheer.png" },
    greeting: "Welcome back. Here's your household.",
    palette: { accent: "#0F766E", accentSoft: "#0F766E1a", onAccent: "#ffffff", tint1: "#0F766E", tint2: "#14B8A6", chip: "#0F766E" },
    dark: { accent: "#2dd4bf", accentSoft: "#2dd4bf24", onAccent: "#08201d", tint1: "#2dd4bf", tint2: "#14B8A6", chip: "#2dd4bf" },
    accent2: "#14B8A6",
    ambient: null,
    card: { from: "#EAF4F2", to: "#F6FAF8" },
    palette_labels: ["PlannioLife Blue", "White", "Soft Grey", "Navy", "Gentle Teal"],
    swatches: ["#0F766E", "#14B8A6", "#1E3A8A", "#CBD5E1", "#F8FAFC"],
  },
  {
    id: "christmas",
    name: "Classic Christmas",
    emoji: "🎄",
    description: "Traditional forest green, deep red and warm gold.",
    inspiration: "Traditional British Christmas.",
    seasonal: true, window: { startM: 11, startD: 20, endM: 1, endD: 6 }, suggestFrom: { m: 11, d: 20 },
    planni: "/themes/planni-christmas-wave.png",
    poses: { wave: "/themes/planni-christmas-wave.png", think: "/themes/planni-christmas-think.png", cheer: "/themes/planni-christmas-cheer.png" },
    greeting: "Welcome back. Everything ready for Christmas?",
    palette: { accent: "#1F5F3A", accentSoft: "#1F5F3A1a", onAccent: "#ffffff", tint1: "#1F5F3A", tint2: "#B22234", chip: "#1F5F3A" },
    dark: { accent: "#6ec18d", accentSoft: "#6ec18d24", onAccent: "#07130c", tint1: "#6ec18d", tint2: "#e06b78", chip: "#6ec18d" },
    accent2: "#B22234",
    ambient: { type: "shimmer", density: 30, colour: "#F6D98B" },
    card: { from: "#E7F0EA", to: "#FBF4EA" },
    palette_labels: ["Forest Green", "Deep Red", "Warm Cream", "Gold", "Soft Pine"],
    swatches: ["#1F5F3A", "#B22234", "#D4AF37", "#355E3B", "#F8F4E8"],
  },
  {
    id: "winter",
    name: "Winter Cabin",
    emoji: "❄️",
    description: "Snowy woodland cabins and cosy evenings.",
    inspiration: "A cosy alpine lodge.",
    seasonal: true, window: { startM: 11, startD: 1, endM: 2, endD: 28 }, suggestFrom: { m: 11, d: 1 },
    planni: "/themes/planni-winter-wave.png",
    poses: { wave: "/themes/planni-winter-wave.png", think: "/themes/planni-winter-think.png", cheer: "/themes/planni-winter-cheer.png" },
    greeting: "Cosy in here. Here's where things stand.",
    palette: { accent: "#4B6F5A", accentSoft: "#4B6F5A1a", onAccent: "#ffffff", tint1: "#9CCCE8", tint2: "#E6A85A", chip: "#4B6F5A" },
    dark: { accent: "#9CCCE8", accentSoft: "#9CCCE824", onAccent: "#08202a", tint1: "#9CCCE8", tint2: "#E6A85A", chip: "#9CCCE8" },
    accent2: "#E6A85A",
    ambient: { type: "snow", density: 64, colour: "#eaf6ff" },
    card: { from: "#E3F1FA", to: "#F4F0EA" },
    palette_labels: ["Ice Blue", "Snow White", "Pine Green", "Slate Grey", "Soft Silver"],
    swatches: ["#9CCCE8", "#DDEFF8", "#4B6F5A", "#5E6E7E", "#C8D0D9"],
  },
  {
    id: "gingerbread",
    name: "Gingerbread Village",
    emoji: "🍪",
    description: "Gingerbread houses and Christmas baking.",
    inspiration: "A warm bakery.",
    seasonal: true, window: { startM: 12, startD: 1, endM: 12, endD: 31 }, suggestFrom: { m: 12, d: 1 },
    planni: "/themes/planni-gingerbread-wave.png",
    poses: { wave: "/themes/planni-gingerbread-wave.png", think: "/themes/planni-gingerbread-think.png", cheer: "/themes/planni-gingerbread-cheer.png" },
    greeting: "Something's baking. Here's your list.",
    palette: { accent: "#8B5A2B", accentSoft: "#8B5A2B1a", onAccent: "#ffffff", tint1: "#C68642", tint2: "#F8D7DA", chip: "#8B5A2B" },
    dark: { accent: "#d69a5f", accentSoft: "#d69a5f24", onAccent: "#1f1405", tint1: "#d69a5f", tint2: "#F8D7DA", chip: "#d69a5f" },
    accent2: "#D9534F",
    ambient: { type: "sugar", density: 34, colour: "#fff6ea" },
    card: { from: "#F3E7D8", to: "#FBEFE9" },
    palette_labels: ["Caramel", "Cinnamon", "Chocolate", "Cream", "Icing Pink"],
    swatches: ["#C68642", "#A0522D", "#5C3A21", "#FFF8EE", "#F8D7DA"],
  },
  {
    id: "spring",
    name: "Spring Garden",
    emoji: "🌸",
    description: "Blossom, wildflowers and fresh beginnings.",
    inspiration: "A fresh countryside garden.",
    seasonal: true, window: { startM: 3, startD: 1, endM: 5, endD: 31 }, suggestFrom: { m: 3, d: 1 },
    planni: "/themes/planni-spring-wave.png",
    poses: { wave: "/themes/planni-spring-wave.png", think: "/themes/planni-spring-think.png", cheer: "/themes/planni-spring-cheer.png" },
    greeting: "Spring is here. Time for a fresh start.",
    palette: { accent: "#5f8a3f", accentSoft: "#5f8a3f1a", onAccent: "#ffffff", tint1: "#A8C686", tint2: "#F7C6D9", chip: "#5f8a3f" },
    dark: { accent: "#A8C686", accentSoft: "#A8C68624", onAccent: "#131f07", tint1: "#A8C686", tint2: "#F7C6D9", chip: "#A8C686" },
    accent2: "#F7C6D9",
    ambient: { type: "petals", density: 30, colour: "#f7c6d9" },
    card: { from: "#EEF4E3", to: "#FBEFF4" },
    palette_labels: ["Sage Green", "Blossom Pink", "Lavender", "Soft Yellow", "Sky Blue"],
    swatches: ["#A8C686", "#F7C6D9", "#B8A7D9", "#FFE680", "#CDEFFF"],
  },
  {
    id: "summer",
    name: "Summer Escape",
    emoji: "☀️",
    description: "Sunshine, coast and long summer evenings.",
    inspiration: "A relaxed Mediterranean coast.",
    seasonal: true, window: { startM: 6, startD: 1, endM: 8, endD: 31 }, suggestFrom: { m: 6, d: 1 },
    planni: "/themes/planni-summer-wave.png",
    poses: { wave: "/themes/planni-summer-wave.png", think: "/themes/planni-summer-think.png", cheer: "/themes/planni-summer-cheer.png" },
    greeting: "Hope you're enjoying the sunshine.",
    palette: { accent: "#2F6690", accentSoft: "#2F66901a", onAccent: "#ffffff", tint1: "#4DA8DA", tint2: "#FF8A65", chip: "#2F6690" },
    dark: { accent: "#4DA8DA", accentSoft: "#4DA8DA24", onAccent: "#07192a", tint1: "#4DA8DA", tint2: "#FF8A65", chip: "#4DA8DA" },
    accent2: "#FF8A65",
    ambient: { type: "clouds", density: 8, colour: "#ffffff" },
    card: { from: "#E4F2FA", to: "#FBF4E4" },
    palette_labels: ["Ocean Blue", "Sandy Beige", "Seafoam", "Soft Yellow", "Coral"],
    swatches: ["#4DA8DA", "#7FD8BE", "#EFD9A7", "#FFD54F", "#FF8A65"],
  },
  {
    id: "autumn",
    name: "Autumn Cottage",
    emoji: "🍂",
    description: "Cosy knits, falling leaves and warm evenings.",
    inspiration: "A cosy countryside cottage.",
    seasonal: true, window: { startM: 9, startD: 1, endM: 11, endD: 30 }, suggestFrom: { m: 9, d: 1 },
    planni: "/themes/planni-autumn-wave.png",
    poses: { wave: "/themes/planni-autumn-wave.png", think: "/themes/planni-autumn-think.png", cheer: "/themes/planni-autumn-cheer.png" },
    greeting: "Perfect weather for getting organised.",
    palette: { accent: "#C96C2D", accentSoft: "#C96C2D1a", onAccent: "#ffffff", tint1: "#C96C2D", tint2: "#7A8450", chip: "#C96C2D" },
    dark: { accent: "#E28743", accentSoft: "#E2874324", onAccent: "#1c0f06", tint1: "#E28743", tint2: "#9aa76a", chip: "#E28743" },
    accent2: "#7A8450",
    ambient: { type: "leaves", density: 34, colour: "#C96C2D" },
    card: { from: "#F6E8DB", to: "#F5F0E4" },
    palette_labels: ["Burnt Orange", "Olive Green", "Warm Brown", "Cream", "Mustard"],
    swatches: ["#C96C2D", "#7A8450", "#6F4E37", "#F5EFE3", "#D4A017"],
  },
  {
    id: "halloween",
    name: "Cosy Halloween",
    emoji: "🎃",
    description: "Friendly pumpkins and autumn evenings.",
    inspiration: "An autumn evening, not horror.",
    seasonal: true, window: { startM: 10, startD: 15, endM: 11, endD: 2 }, suggestFrom: { m: 10, d: 15 },
    planni: "/themes/planni-halloween-wave.png",
    poses: { wave: "/themes/planni-halloween-wave.png", think: "/themes/planni-halloween-think.png", cheer: "/themes/planni-halloween-cheer.png" },
    greeting: "No tricks today, just reminders.",
    palette: { accent: "#6A4C93", accentSoft: "#6A4C931a", onAccent: "#ffffff", tint1: "#F28C28", tint2: "#6A4C93", chip: "#6A4C93" },
    dark: { accent: "#F28C28", accentSoft: "#F28C2824", onAccent: "#170a1f", tint1: "#F28C28", tint2: "#a98cd0", chip: "#F28C28" },
    accent2: "#F28C28",
    ambient: { type: "ghosts", density: 12, colour: "#ffffff" },
    card: { from: "#F7E9DB", to: "#ECE6F2" },
    palette_labels: ["Pumpkin Orange", "Deep Purple", "Midnight Blue", "Cream", "Soft Black"],
    swatches: ["#F28C28", "#6A4C93", "#1C2541", "#F4C430", "#FFF6E8"],
  },
  {
    id: "pride",
    name: "Pride",
    emoji: "🏳️‍🌈",
    description: "A tasteful, inclusive celebration of Pride.",
    inspiration: "Elegant, positive and celebratory.",
    seasonal: true, window: { startM: 6, startD: 1, endM: 6, endD: 30 }, suggestFrom: { m: 6, d: 1 }, yearRound: true,
    planni: "/themes/planni-pride-wave.png",
    poses: { wave: "/themes/planni-pride-wave.png", think: "/themes/planni-pride-think.png", cheer: "/themes/planni-pride-cheer.png" },
    greeting: "Wishing everyone a happy Pride.",
    palette: { accent: "#2563EB", accentSoft: "#2563EB1a", onAccent: "#ffffff", tint1: "#E40303", tint2: "#750787", chip: "#2563EB", pride: true },
    dark: { accent: "#60a5fa", accentSoft: "#60a5fa24", onAccent: "#08152a", tint1: "#FF8C00", tint2: "#750787", chip: "#60a5fa", pride: true },
    accent2: "#750787",
    ambient: { type: "shimmer", density: 18, colour: "#ffffff" },
    card: { from: "#EAF0FE", to: "#F3ECFA", pride: true },
    palette_labels: ["PlannioLife Blue", "White", "Rainbow Accents", "Soft Pastels", "Neutral"],
    swatches: ["#E40303", "#FF8C00", "#FFED00", "#008026", "#750787"],
  },
];

export const THEME_MAP = Object.fromEntries(THEMES.map((t) => [t.id, t]));
export const getTheme = (id) => THEME_MAP[id] || THEME_MAP.default;

// Is a month/day inside a (possibly year-wrapping) window?
function inWindow(now, w) {
  if (!w) return false;
  const md = (now.getMonth() + 1) * 100 + now.getDate();
  const start = w.startM * 100 + w.startD;
  const end = w.endM * 100 + w.endD;
  return start <= end ? md >= start && md <= end : md >= start || md <= end;
}

// The single seasonal theme most relevant right now (for suggestions + auto-switch).
// Priority: narrower, event-style windows win over the broad Winter/Autumn ambience so December
// suggests Christmas, not Winter Cabin.
const SUGGEST_PRIORITY = ["christmas", "gingerbread", "halloween", "pride", "spring", "summer", "autumn", "winter"];
export function currentSeasonalTheme(now = new Date()) {
  for (const id of SUGGEST_PRIORITY) {
    const t = THEME_MAP[id];
    if (t?.seasonal && inWindow(now, t.window)) return t;
  }
  return null;
}

// Every seasonal theme currently in season (for the gallery "In season" badge).
export function inSeasonIds(now = new Date()) {
  return THEMES.filter((t) => t.seasonal && inWindow(now, t.window)).map((t) => t.id);
}

// A stable per-season key so a dismissal only lasts for the current occurrence of that season.
export function seasonKey(theme, now = new Date()) {
  if (!theme) return "";
  const yr = now.getMonth() + 1 < theme.window.startM && theme.window.startM > theme.window.endM
    ? now.getFullYear() - 1 : now.getFullYear();
  return `${theme.id}-${yr}`;
}
